import { useMemo, useState } from 'react';
import { CriarAlvoSchema, type AlvoDoToolkit, type EstadoNoAlvo } from '@remoto/shared';
import { Modal } from '../componentes/Modal';
import { IconeMais } from '../componentes/icones';
import { mensagemDoErro } from '../erros';
import { GESTOS, usarAlvos, usarGesto, usarRotinas } from './consultas';
import { AvisoDeErro, BotaoPrimario, BotaoSecundario, CampoDeLinha, usarPodeComandar } from './pecas';
import { validar } from './regras';
import { SeloDoEstado, SeloNeutro } from './selos';

/**
 * A aba Alvos: os sistemas externos onde uma rotina roda — um tribunal num
 * sistema e grau, ou qualquer sistema (vocabulário neutro, F3). A família é
 * um agrupamento DECLARADO: "mesma família = mesmo fluxo" falhou 16 vezes na
 * JusAPI, e por isso ela aqui é só rótulo — quem estende uma rotina a outro
 * alvo é uma decisão com a semelhança por extenso (D31).
 *
 * Alvo não se apaga: desativa-se (o histórico de validações aponta para ele).
 */

function DialogoDeAlvo({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }): React.JSX.Element | null {
  const criar = usarGesto(GESTOS.criarAlvo);
  const [campos, setCampos] = useState({ chave: '', titulo: '', sistema: '', familia: '', grau: '' });
  const [problemas, setProblemas] = useState<string[]>([]);
  const mudar = (parcial: Partial<typeof campos>): void => setCampos((c) => ({ ...c, ...parcial }));

  function gravar(): void {
    const r = validar(CriarAlvoSchema, {
      chave: campos.chave.trim(),
      titulo: campos.titulo,
      sistema: campos.sistema,
      ...(campos.familia.trim() ? { familia: campos.familia } : {}),
      ...(campos.grau.trim() ? { grau: campos.grau } : {}),
    });
    if (!r.ok) return setProblemas(r.problemas);
    setProblemas([]);
    criar.mutate(r.dados, { onSuccess: aoFechar });
  }

  return (
    <Modal
      titulo="Novo alvo"
      aberto={aberto}
      aoFechar={aoFechar}
      rodape={
        <>
          <BotaoSecundario onClick={aoFechar}>Cancelar</BotaoSecundario>
          <BotaoPrimario disabled={criar.isPending} onClick={gravar}>
            Criar alvo
          </BotaoPrimario>
        </>
      }
    >
      <div className="space-y-3">
        <CampoDeLinha
          rotulo="Chave"
          dica="minúsculas, dígitos e hífen — é o nome que as sessões digitam (ex.: jfsc-eproc-1g)"
          value={campos.chave}
          onChange={(e) => mudar({ chave: e.target.value })}
          autoCapitalize="off"
          spellCheck={false}
        />
        <CampoDeLinha rotulo="Título" value={campos.titulo} onChange={(e) => mudar({ titulo: e.target.value })} maxLength={200} />
        <CampoDeLinha rotulo="Sistema" value={campos.sistema} onChange={(e) => mudar({ sistema: e.target.value })} maxLength={100} />
        <div className="grid gap-3 sm:grid-cols-2">
          <CampoDeLinha
            rotulo="Família (opcional)"
            value={campos.familia}
            onChange={(e) => mudar({ familia: e.target.value })}
            maxLength={100}
          />
          <CampoDeLinha rotulo="Grau (opcional)" value={campos.grau} onChange={(e) => mudar({ grau: e.target.value })} maxLength={40} />
        </div>
        {problemas.length > 0 ? (
          <AvisoDeErro>
            {problemas.map((p) => (
              <span key={p} className="block">
                {p}
              </span>
            ))}
          </AvisoDeErro>
        ) : null}
        {criar.error ? <AvisoDeErro>{mensagemDoErro(criar.error, 'Não foi possível criar o alvo agora.')}</AvisoDeErro> : null}
      </div>
    </Modal>
  );
}

function LinhaDoAlvo({
  alvo,
  estados,
  podeComandar,
}: {
  alvo: AlvoDoToolkit;
  estados: { rotina: string; estado: EstadoNoAlvo }[];
  podeComandar: boolean;
}): React.JSX.Element {
  const ativar = usarGesto(GESTOS.ativarAlvo);
  return (
    <li data-testid="alvo" data-alvo={alvo.chave} className="min-w-0 rounded-lg border border-zinc-800 p-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="break-all font-mono text-sm text-zinc-100">{alvo.chave}</span>
        <span className="min-w-0 break-words text-sm text-zinc-300">{alvo.titulo}</span>
        {alvo.ativo ? null : <SeloNeutro>desativado</SeloNeutro>}
      </div>
      <p className="mt-1 break-words text-xs text-zinc-500">
        {alvo.sistema}
        {alvo.familia ? ` · família ${alvo.familia}` : ''}
        {alvo.grau ? ` · ${alvo.grau}` : ''}
      </p>
      {estados.length > 0 ? (
        <ul className="mt-2 flex min-w-0 flex-wrap gap-1.5" aria-label={'Rotinas em ' + alvo.chave}>
          {estados.map((e) => (
            <li key={e.rotina} className="min-w-0 max-w-full">
              <SeloDoEstado estado={e.estado} prefixo={e.rotina} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[11px] text-zinc-500">nenhuma rotina estendida a este alvo</p>
      )}
      {podeComandar ? (
        <div className="mt-2">
          <BotaoSecundario
            className="text-xs"
            disabled={ativar.isPending}
            onClick={() => ativar.mutate({ id: alvo.id, ativo: !alvo.ativo })}
          >
            {alvo.ativo ? 'Desativar' : 'Reativar'}
          </BotaoSecundario>
        </div>
      ) : null}
      {ativar.error ? <AvisoDeErro>{mensagemDoErro(ativar.error, 'Não foi possível mudar o alvo agora.')}</AvisoDeErro> : null}
    </li>
  );
}

export function AbaAlvos(): React.JSX.Element {
  const podeComandar = usarPodeComandar();
  const alvos = usarAlvos();
  const rotinas = usarRotinas(false);
  const [criando, setCriando] = useState(false);
  // O estado de cada rotina em cada alvo, lido da matriz que a lista de rotinas já traz.
  const porAlvo = useMemo(() => {
    const mapa = new Map<string, { rotina: string; estado: EstadoNoAlvo }[]>();
    for (const r of rotinas.data ?? []) {
      for (const c of r.matriz) mapa.set(c.alvo, [...(mapa.get(c.alvo) ?? []), { rotina: r.nome, estado: c.estado }]);
    }
    return mapa;
  }, [rotinas.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-zinc-500">
          Um caminho validado num alvo não vale para outro sem decisão: a família é só um agrupamento declarado.
        </p>
        {podeComandar ? (
          <BotaoPrimario onClick={() => setCriando(true)}>
            <IconeMais tamanho={14} />
            Novo alvo
          </BotaoPrimario>
        ) : null}
      </div>
      {alvos.isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {alvos.error ? <AvisoDeErro>{mensagemDoErro(alvos.error, 'Não foi possível carregar os alvos agora.')}</AvisoDeErro> : null}
      {alvos.data && alvos.data.length === 0 ? <p className="text-sm text-zinc-500">Nenhum alvo cadastrado ainda.</p> : null}
      {alvos.data && alvos.data.length > 0 ? (
        <ul data-testid="lista-de-alvos" className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {alvos.data.map((a) => (
            <LinhaDoAlvo key={a.id} alvo={a} estados={porAlvo.get(a.chave) ?? []} podeComandar={podeComandar} />
          ))}
        </ul>
      ) : null}
      {criando ? <DialogoDeAlvo aberto aoFechar={() => setCriando(false)} /> : null}
    </div>
  );
}
